// src/pages/CustomerDue/components/exportCustomerDueCsv.js
import commaNumber from "comma-number";

const format = commaNumber.bindWith(",", ".");

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

/**
 * @param {object} params
 * @param {Array}   params.list          - rows from Redux state.customerDue.list
 * @param {number}  params.totalBalance  - from Redux state.customerDue.totalBalance
 * @param {object}  params.filters       - for filterType check (By Tag columns)
 * @param {object}  params.authInfo      - for Pharmacy columns
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportCustomerDueCsv = ({
  list,
  totalBalance,
  filters,
  authInfo,
  title = "Customer Due Balance Report",
  filename = "customer_due_balance",
}) => {
  const isPharmacy = authInfo?.branch_company_type === "Pharmacy";
  const isByTag    = filters?.filterType === "By Tag";

  // ── Build header row ──────────────────────────────────────────
  const headers = [
    "No.", "Code", "Customer Name", "Contact No", "Address",
    "Opening", "Bill Amount", "Received", "Payment", "Return", "Discount", "Balance",
    ...(isPharmacy ? ["Insurance Amount", "Insurance Amount Paid"] : []),
    ...(isByTag    ? ["Tag Group", "Tag Field"]                    : []),
  ].map(escape);

  // ── Build data rows ───────────────────────────────────────────
  const dataRows = list.map((row, i) => [
    i + 1,
    row.acc_code        || "",
    row.acc_name        || "",
    row.contact_no      || "",
    row.address         || "",
    format(parseFloat(row.opening_balance      || 0).toFixed(2)),
    format(parseFloat(row.total_bill_amount    || 0).toFixed(2)),
    format(parseFloat(row.total_received_amount|| 0).toFixed(2)),
    format(parseFloat(row.total_payment        || 0).toFixed(2)),
    format(parseFloat(row.return_amount        || 0).toFixed(2)),
    format(parseFloat(row.discount_amount      || 0).toFixed(2)),
    format(parseFloat(row.balance              || 0).toFixed(2)),
    ...(isPharmacy ? [
      format(parseFloat(row.insurance_due_amount      || 0).toFixed(2)),
      format(parseFloat(row.insurance_amount_received || 0).toFixed(2)),
    ] : []),
    ...(isByTag ? [
      row.tag_group || "",
      row.tag_field || "",
    ] : []),
  ].map(escape));

  // ── Build totals row (mirrors table summary) ──────────────────
  const totals = list.reduce((acc, row) => ({
    opening:         acc.opening         + parseFloat(row.opening_balance       || 0),
    billAmount:      acc.billAmount      + parseFloat(row.total_bill_amount     || 0),
    received:        acc.received        + parseFloat(row.total_received_amount || 0),
    payment:         acc.payment         + parseFloat(row.total_payment         || 0),
    return:          acc.return          + parseFloat(row.return_amount         || 0),
    discount:        acc.discount        + parseFloat(row.discount_amount       || 0),
    insuranceDue:    acc.insuranceDue    + parseFloat(row.insurance_due_amount      || 0),
    insuranceCredit: acc.insuranceCredit + parseFloat(row.insurance_amount_received || 0),
  }), { opening: 0, billAmount: 0, received: 0, payment: 0,
        return: 0, discount: 0, insuranceDue: 0, insuranceCredit: 0 });

  const totalRow = [
    "Grand Total", "", "", "", "",
    format(totals.opening.toFixed(2)),
    format(totals.billAmount.toFixed(2)),
    format(totals.received.toFixed(2)),
    format(totals.payment.toFixed(2)),
    format(totals.return.toFixed(2)),
    format(totals.discount.toFixed(2)),
    format(totalBalance.toFixed(2)),
    ...(isPharmacy ? [
      format(totals.insuranceDue.toFixed(2)),
      format(totals.insuranceCredit.toFixed(2)),
    ] : []),
    ...(isByTag ? ["", ""] : []),
  ].map(escape);

  // ── Assemble and trigger download ─────────────────────────────
  const rows = [
    [title],
    [],
    headers,
    ...dataRows,
    [],
    totalRow,
  ];

  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};